import {
  Game,
  GameState as APIGameState,
} from "@aura-frontend/omnibus-az-client";
import { useDispatch } from "react-redux";

import {
  setCurrentCatcher,
  setCurrentHitter,
  setCurrentLineup,
  setCurrentPitcher,
  setGameState,
} from "../store/slices/game";
import { getHomeAwayLineups, mapLineupEntryToIPLayer } from "./api-helpers";

/**
 * Hook to update the game state in the redux store. This is called after receiving a updated GameState from the server.
 * @returns A function to update the game state in the redux store
 */
export const useUpdateGameState = () => {
  const dispatch = useDispatch();

  /**
   * Updates the game state in the redux store
   * @param gameState - The new game state
   * @param skipGameState - When true, skip dispatching setGameState and current player updates (used for substitutions where the caller sets players explicitly)
   */
  return (
    APIGameState?: APIGameState,
    APIGame?: Game,
    skipGameState = false,
  ) => {
    if (!APIGameState || !APIGame) return;

    if (!skipGameState) {
      // Updating local game state
      const gameState = {
        paOfInning: APIGameState.paOfInning,
        pitchOfPa: APIGameState.pitchOfPa,
        awayScore: APIGameState.awayScore,
        homeScore: APIGameState.homeScore,
        inning: {
          integer: APIGameState.inning.integer,
          half: APIGameState.inning.half,
        },
        count: {
          balls: APIGameState.count.balls,
          strikes: APIGameState.count.strikes,
        },
        outs: APIGameState.outs,
      };
      dispatch(setGameState(gameState));

      // Updating players
      if (APIGameState.currentHitter) {
        const player = mapLineupEntryToIPLayer(APIGameState.currentHitter);
        const hitterTeam = APIGameState.inning.half === "top" ? "away" : "home";
        dispatch(
          setCurrentHitter({
            team: hitterTeam,
            player: {
              ...player,
              position: Number(APIGameState.currentHitter.position),
            },
          }),
        );
      }

      if (APIGameState.currentCatcher) {
        const player = mapLineupEntryToIPLayer(APIGameState.currentCatcher);
        const catcherTeam =
          APIGameState.inning.half === "top" ? "home" : "away";
        dispatch(setCurrentCatcher({ team: catcherTeam, player }));
      }

      if (APIGameState.currentPitcher) {
        const player = mapLineupEntryToIPLayer(APIGameState.currentPitcher);
        const pitcherTeam =
          APIGameState.inning.half === "top" ? "home" : "away";
        dispatch(setCurrentPitcher({ team: pitcherTeam, player }));
      }
    }

    const { home: homeLineup, away: awayLineup } = getHomeAwayLineups(APIGame);

    dispatch(
      setCurrentLineup({
        team: "away",
        lineup: awayLineup.map(mapLineupEntryToIPLayer),
      }),
    );

    dispatch(
      setCurrentLineup({
        team: "home",
        lineup: homeLineup.map(mapLineupEntryToIPLayer),
      }),
    );
  };
};
